import { useState, useMemo } from "react";
import {
  ChevronLeft, ChevronRight, X, CheckCircle, Circle,
  BookOpen, Calculator, HelpCircle, Lightbulb, AlertTriangle,
  Check, TrendingUp,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// LessonPlayer
//
// A reusable lesson modal that renders inside the TrainingTab.
// Data-driven: pass a `module` object from TRAINING_DATA and it handles
// concept slides, calculators, and quiz questions automatically.
//
// Props:
//   module   – one module object from TRAINING_DATA (must have .lessonData)
//   onClose  – called when user closes or completes the module
//   onComplete – called when user clicks "Complete Module"
//
// Each lesson in module.lessonData has:
//   { id, title, type: "concept"|"calculator"|"quiz", icon?, accentColor?,
//     headline, content: { ... type-specific fields ... } }
// ─────────────────────────────────────────────────────────────────────────────

// ── Shared sub-components ─────────────────────────────────────────────────────

function Callout({ type = "tip", text }) {
  const isWarn = type === "warning";
  return (
    <div style={{
      display: "flex", gap: 10, padding: "11px 14px", marginTop: 16,
      background: isWarn ? "#fffbeb" : "#eff6ff",
      borderLeft: `3px solid ${isWarn ? "#ca8a04" : "#2563eb"}`,
      borderRadius: "0 8px 8px 0",
    }}>
      {isWarn
        ? <AlertTriangle size={14} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
        : <Lightbulb size={14} color="#2563eb" style={{ flexShrink: 0, marginTop: 1 }} />}
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.65,
        color: isWarn ? "#92400e" : "#1e40af" }}
        dangerouslySetInnerHTML={{ __html: `<strong>${isWarn ? "Watch out" : "Pro tip"}:</strong> ${text}` }}
      />
    </div>
  );
}

function LessonBody({ text }) {
  return (
    <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.75, margin: "0 0 10px" }}
      dangerouslySetInnerHTML={{ __html: text }} />
  );
}

// ── Lesson type renderers ─────────────────────────────────────────────────────

function ConceptRenderer({ content }) {
  return (
    <div>
      {content.paragraphs?.map((p, i) => <LessonBody key={i} text={p} />)}

      {content.formula && (
        <div style={{
          margin: "18px 0", padding: "18px 22px",
          background: "#0f172a", borderRadius: 10, textAlign: "center",
        }}>
          <div style={{ fontSize: 10, color: "#64748b", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
            {content.formula.label}
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#fff", fontFamily: "monospace" }}>
            {content.formula.expression}
          </div>
          {content.formula.sub && (
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 5 }}>{content.formula.sub}</div>
          )}
        </div>
      )}

      {content.breakdown && (
        <div style={{ margin: "16px 0" }}>
          {content.breakdown.map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: "1px solid #f1f5f9" }}>
              {item.letter && (
                <div style={{
                  width: 30, height: 30, borderRadius: 7, background: "#0f172a",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, fontWeight: 700, color: "#f8fafc", flexShrink: 0, fontFamily: "monospace",
                }}>{item.letter}</div>
              )}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{item.word ?? item.label}</div>
                <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 1 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {content.thresholds && (
        <div style={{ margin: "16px 0", display: "flex", flexDirection: "column", gap: 8 }}>
          {content.thresholds.map((t, i) => (
            <div key={i} style={{ padding: "11px 14px", borderRadius: 8, background: t.bg, border: `1px solid ${t.border}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: t.color, fontFamily: "monospace" }}>{t.range}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: t.color, background: t.color + "18", padding: "1px 9px", borderRadius: 20 }}>{t.label}</span>
              </div>
              <p style={{ margin: 0, fontSize: 12.5, color: "#475569", lineHeight: 1.6 }}>{t.desc}</p>
            </div>
          ))}
        </div>
      )}

      {content.examples && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "16px 0" }}>
          {content.examples.map((ex, i) => (
            <div key={i} style={{ padding: "13px 15px", borderRadius: 10, background: ex.bg, border: `1px solid ${ex.color}28` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                {ex.emoji && <span style={{ fontSize: 16 }}>{ex.emoji}</span>}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1e293b" }}>{ex.name}</div>
                  {ex.borrower && <div style={{ fontSize: 11.5, color: "#64748b" }}>{ex.borrower}</div>}
                </div>
                {ex.dscr !== undefined && (
                  <span style={{ fontSize: 20, fontWeight: 800, color: ex.color, fontFamily: "monospace" }}>
                    {ex.dscr.toFixed(2)}
                  </span>
                )}
                {ex.metric && (
                  <span style={{ fontSize: 13, fontWeight: 700, color: ex.color, fontFamily: "monospace" }}>
                    {ex.metric}
                  </span>
                )}
              </div>
              {ex.detail && <div style={{ fontSize: 11.5, color: "#64748b", fontFamily: "monospace", background: "rgba(255,255,255,0.5)", padding: "3px 7px", borderRadius: 5, display: "inline-block", marginBottom: 6 }}>{ex.detail}</div>}
              {ex.outcome && <p style={{ margin: 0, fontSize: 12.5, color: "#374151", lineHeight: 1.6 }}>{ex.outcome}</p>}
            </div>
          ))}
        </div>
      )}

      {content.programs && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "16px 0" }}>
          {content.programs.map((p, i) => (
            <div key={i} style={{ display: "flex", gap: 12, padding: "12px 14px", border: `1px solid ${p.color}28`, borderRadius: 9 }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: p.color, color: "#fff", fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: "#1e293b" }}>{p.name}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: p.color, background: p.color + "18", padding: "1px 7px", borderRadius: 20 }}>{p.badge}</span>
                  <span style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 700, color: p.color }}>{p.ltv}</span>
                </div>
                {p.min && <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace", marginBottom: 4 }}>{p.min}</div>}
                <p style={{ margin: 0, fontSize: 12.5, color: "#475569", lineHeight: 1.6 }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {content.list && (
        <div style={{ margin: "14px 0" }}>
          {content.list.map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 10, padding: "6px 0", borderBottom: "1px solid #f1f5f9" }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#2563eb", marginTop: 7, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{ __html: item }} />
            </div>
          ))}
        </div>
      )}

      {content.table && (
        <div style={{ margin: "14px 0", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                {content.table.headers.map((h, i) => (
                  <th key={i} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#64748b", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {content.table.rows.map((row, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  {row.map((cell, j) => (
                    <td key={j} style={{ padding: "8px 12px", color: j === 0 ? "#1e293b" : "#475569", fontWeight: j === 0 ? 600 : 400 }}
                      dangerouslySetInnerHTML={{ __html: cell }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {content.callout && <Callout type={content.callout.type} text={content.callout.text} />}
    </div>
  );
}

// ── ResultPanel — renders plain-object output from content.renderResults() ────

function ResultPanel({ result }) {
  if (!result) return null;
  const { highlight, rows, tip } = result;
  const barColor = highlight?.color ?? "#2563eb";
  return (
    <div>
      <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px 18px", marginBottom: 0 }}>
        {highlight && (
          <div style={{ marginBottom: rows?.length ? 12 : 0 }}>
            <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 }}>
              {highlight.label}
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: barColor, fontFamily: "monospace", lineHeight: 1 }}>
              {highlight.value}
            </div>
            {highlight.barPct !== undefined && (
              <div style={{ marginTop: 8 }}>
                <div style={{ height: 6, background: "#1e293b", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ width: `${Math.min(highlight.barPct, 100)}%`, height: "100%", background: barColor, borderRadius: 3, transition: "width 0.3s" }} />
                </div>
                {highlight.barLabels && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    {highlight.barLabels.map((l, i) => (
                      <span key={i} style={{ fontSize: 10, color: i === 1 ? "#16a34a" : "#475569" }}>{l}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {rows?.length > 0 && (
          <div style={{ borderTop: highlight ? "1px solid #1e293b" : "none", paddingTop: highlight ? 10 : 0, display: "flex", flexWrap: "wrap", gap: "8px 20px" }}>
            {rows.map((r, i) => (
              <div key={i}>
                <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.07em" }}>{r.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: r.color ?? "#f8fafc", fontFamily: "monospace" }}>{r.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {tip && (
        <div style={{ marginTop: 10, padding: "9px 12px", background: "#eff6ff", borderLeft: "3px solid #2563eb", borderRadius: "0 7px 7px 0", fontSize: 12.5, color: "#1e40af", lineHeight: 1.6 }}>
          💡 {tip}
        </div>
      )}
    </div>
  );
}

// ── Calculator renderer ───────────────────────────────────────────────────────

function CalculatorRenderer({ content }) {
  const initVals = useMemo(() => Object.fromEntries(content.fields.map(f => [f.key, f.default])), []);
  const [vals, setVals] = useState(initVals);
  const results = useMemo(() => content.compute(vals), [vals]);
  // renderResults returns a plain object { highlight, rows, tip }
  const panel   = useMemo(() => content.renderResults(results), [results]);

  return (
    <div>
      {content.intro && <LessonBody text={content.intro} />}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))", gap: "10px 14px", marginBottom: 18 }}>
        {content.fields.map(f => (
          <div key={f.key}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>
              {f.label}
            </label>
            <input
              type="number"
              value={vals[f.key]}
              step={f.step ?? 1}
              min={f.min ?? 0}
              max={f.max}
              onChange={e => setVals(v => ({ ...v, [f.key]: parseFloat(e.target.value) || 0 }))}
              style={{ width: "100%", padding: "7px 9px", borderRadius: 7, border: "1px solid #cbd5e1", fontSize: 13, color: "#1e293b", background: "#fff", boxSizing: "border-box", outline: "none" }}
            />
          </div>
        ))}
      </div>

      <ResultPanel result={panel} />

      {content.callout && <Callout type={content.callout.type} text={content.callout.text} />}
    </div>
  );
}

// ── Quiz renderer ─────────────────────────────────────────────────────────────

function QuizRenderer({ content, onPass }) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const score = submitted
    ? content.questions.reduce((acc, q) => acc + (answers[q.id] === q.correct ? 1 : 0), 0)
    : 0;
  const passed = score >= Math.ceil(content.questions.length * (content.passPct ?? 0.7));

  function submit() {
    if (Object.keys(answers).length < content.questions.length) return;
    setSubmitted(true);
    if (passed) onPass?.();
  }

  return (
    <div>
      {content.intro && <LessonBody text={content.intro} />}

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {content.questions.map((q, qi) => {
          const selected = answers[q.id];
          const isCorrect = submitted && selected === q.correct;
          const isWrong = submitted && selected !== undefined && selected !== q.correct;

          return (
            <div key={q.id} style={{ padding: "14px 16px", borderRadius: 10, background: submitted ? (isCorrect ? "#f0fdf4" : "#fef2f2") : "#f8fafc", border: `1px solid ${submitted ? (isCorrect ? "#bbf7d0" : "#fecaca") : "#e2e8f0"}` }}>
              <p style={{ margin: "0 0 10px", fontSize: 13.5, fontWeight: 600, color: "#1e293b" }}>
                <span style={{ color: "#94a3b8", marginRight: 6 }}>{qi + 1}.</span>
                {q.question}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {q.options.map((opt, oi) => {
                  const isSelected = selected === oi;
                  const isAnswer = submitted && oi === q.correct;
                  return (
                    <button key={oi}
                      disabled={submitted}
                      onClick={() => !submitted && setAnswers(a => ({ ...a, [q.id]: oi }))}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "8px 12px", borderRadius: 7, border: "1px solid",
                        borderColor: isAnswer ? "#16a34a" : isSelected && !submitted ? "#2563eb" : "#e2e8f0",
                        background: isAnswer ? "#f0fdf4" : isSelected && !submitted ? "#eff6ff" : "#fff",
                        cursor: submitted ? "default" : "pointer", textAlign: "left",
                      }}>
                      <div style={{
                        width: 18, height: 18, borderRadius: "50%", border: "2px solid",
                        borderColor: isAnswer ? "#16a34a" : isSelected ? "#2563eb" : "#cbd5e1",
                        background: isAnswer ? "#16a34a" : isSelected && !submitted ? "#2563eb" : "transparent",
                        flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {(isAnswer || (isSelected && !submitted)) && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
                      </div>
                      <span style={{ fontSize: 13, color: "#374151" }}>{opt}</span>
                    </button>
                  );
                })}
              </div>
              {submitted && q.explanation && (
                <p style={{ margin: "10px 0 0", fontSize: 12, color: isCorrect ? "#166534" : "#991b1b", lineHeight: 1.6 }}>
                  {isCorrect ? "✓ " : "✗ "}{q.explanation}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {!submitted ? (
        <button
          onClick={submit}
          disabled={Object.keys(answers).length < content.questions.length}
          className="btn btn-primary"
          style={{ marginTop: 20, opacity: Object.keys(answers).length < content.questions.length ? 0.5 : 1 }}
        >
          Submit Quiz
        </button>
      ) : (
        <div style={{
          marginTop: 20, padding: "16px 18px", borderRadius: 10,
          background: passed ? "#f0fdf4" : "#fef2f2",
          border: `1px solid ${passed ? "#bbf7d0" : "#fecaca"}`,
          display: "flex", alignItems: "center", gap: 14,
        }}>
          <div style={{ fontSize: 28 }}>{passed ? "🎉" : "📚"}</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: passed ? "#166534" : "#991b1b" }}>
              {passed ? "Nice work!" : "Keep studying"} — {score}/{content.questions.length} correct
            </div>
            <div style={{ fontSize: 12.5, color: passed ? "#166534" : "#991b1b", marginTop: 2 }}>
              {passed ? "You passed this quiz. Move to the next lesson." : "Review the lesson and try again."}
            </div>
          </div>
          {!passed && (
            <button className="btn btn-secondary btn-sm" style={{ marginLeft: "auto" }}
              onClick={() => { setAnswers({}); setSubmitted(false); }}>
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main LessonPlayer ─────────────────────────────────────────────────────────

const TYPE_ICONS = {
  concept:    BookOpen,
  calculator: Calculator,
  quiz:       HelpCircle,
};

export default function LessonPlayer({ module, onClose, onComplete }) {
  const lessons = module?.lessonData ?? [];
  const [current, setCurrent]     = useState(0);
  const [completed, setCompleted] = useState(new Set());
  const [quizPassed, setQuizPassed] = useState(new Set());

  if (!lessons.length) return null;

  const lesson = lessons[current];
  const Icon   = TYPE_ICONS[lesson.type] ?? BookOpen;
  const accent = lesson.accentColor ?? module.color ?? "#2563eb";
  const pct    = Math.round((completed.size / lessons.length) * 100);

  function markDone(i = current) {
    setCompleted(c => new Set([...c, lessons[i].id]));
  }

  function goTo(i) {
    markDone();
    setCurrent(i);
  }

  function next() {
    if (current < lessons.length - 1) goTo(current + 1);
  }

  function prev() {
    if (current > 0) setCurrent(current - 1);
  }

  const isLast    = current === lessons.length - 1;
  const isQuiz    = lesson.type === "quiz";
  const quizOk    = !isQuiz || quizPassed.has(lesson.id);

  function renderLesson() {
    switch (lesson.type) {
      case "calculator": return <CalculatorRenderer content={lesson.content} />;
      case "quiz":       return <QuizRenderer content={lesson.content} onPass={() => setQuizPassed(p => new Set([...p, lesson.id]))} />;
      default:           return <ConceptRenderer content={lesson.content} />;
    }
  }

  return (
    // Overlay sits within the card flow — no fixed/portal needed
    <div style={{
      border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden",
      background: "#fff", boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
      display: "flex", minHeight: 520,
    }}>
      {/* ── Sidebar ── */}
      <div style={{ width: 200, flexShrink: 0, background: "#0f172a", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "16px 14px 12px", borderBottom: "1px solid #1e293b" }}>
          <div style={{ fontSize: 9, color: "#475569", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>
            Module
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "#f8fafc", lineHeight: 1.4 }}>
            {module.title}
          </div>
          {/* Progress bar */}
          <div style={{ marginTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 10, color: "#64748b" }}>Progress</span>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#38bdf8" }}>{pct}%</span>
            </div>
            <div style={{ height: 3, background: "#1e293b", borderRadius: 2, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: "#38bdf8", borderRadius: 2, transition: "width 0.4s" }} />
            </div>
          </div>
        </div>

        {/* Lesson list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
          {lessons.map((l, i) => {
            const LIcon  = TYPE_ICONS[l.type] ?? BookOpen;
            const done   = completed.has(l.id);
            const active = i === current;
            return (
              <button key={l.id} onClick={() => goTo(i)} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "8px 14px", background: active ? "#1e293b" : "transparent",
                border: "none", borderLeft: `3px solid ${active ? accent : "transparent"}`,
                cursor: "pointer", textAlign: "left",
              }}>
                <div style={{ flexShrink: 0 }}>
                  {done
                    ? <CheckCircle size={13} color="#22d3ee" />
                    : <Circle size={13} color={active ? "#94a3b8" : "#334155"} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, fontWeight: active ? 600 : 400, color: active ? "#f8fafc" : done ? "#94a3b8" : "#64748b", lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {l.title}
                  </div>
                  <div style={{ fontSize: 10, color: "#334155", marginTop: 1 }}>
                    {l.type === "quiz" ? "Quiz" : l.type === "calculator" ? "Calculator" : "Lesson"}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Close button */}
        <div style={{ padding: "10px 14px", borderTop: "1px solid #1e293b" }}>
          <button onClick={onClose} style={{
            width: "100%", padding: "7px", background: "#1e293b", border: "none",
            borderRadius: 7, color: "#64748b", fontSize: 11.5, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
          }}>
            <X size={11} /> Close
          </button>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Top bar */}
        <div style={{
          padding: "12px 20px", borderBottom: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
        }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: accent + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon size={15} color={accent} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {lesson.type === "quiz" ? "Knowledge Check" : lesson.type === "calculator" ? "Interactive Tool" : `Lesson ${current + 1} of ${lessons.length}`}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {lesson.title}
            </div>
          </div>
          {/* Step dots */}
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {lessons.map((_, i) => (
              <div key={i} onClick={() => goTo(i)} style={{
                width: i === current ? 16 : 5, height: 5, borderRadius: 3,
                background: completed.has(lessons[i].id) ? "#22d3ee" : i === current ? accent : "#e2e8f0",
                cursor: "pointer", transition: "width 0.2s, background 0.2s",
              }} />
            ))}
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
          {lesson.content?.headline && (
            <h2 style={{ fontSize: 16, fontWeight: 700, color: "#1e293b", margin: "0 0 14px", lineHeight: 1.4 }}>
              {lesson.content.headline}
            </h2>
          )}
          {renderLesson()}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 20px", borderTop: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
        }}>
          <button onClick={prev} disabled={current === 0}
            className="btn btn-secondary btn-sm"
            style={{ opacity: current === 0 ? 0.4 : 1 }}>
            <ChevronLeft size={13} /> Back
          </button>

          <span style={{ fontSize: 11.5, color: "#94a3b8" }}>{current + 1} / {lessons.length}</span>

          {isLast ? (
            <button
              onClick={() => { markDone(); onComplete?.(); onClose?.(); }}
              disabled={isQuiz && !quizOk}
              className="btn btn-primary btn-sm"
              style={{ background: "#16a34a", borderColor: "#16a34a", opacity: (isQuiz && !quizOk) ? 0.5 : 1 }}
            >
              <Check size={13} /> Complete Module
            </button>
          ) : (
            <button
              onClick={next}
              disabled={isQuiz && !quizOk}
              className="btn btn-primary btn-sm"
              style={{ background: accent, borderColor: accent, opacity: (isQuiz && !quizOk) ? 0.5 : 1 }}
            >
              Next <ChevronRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}